import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fullDataPiece } from '../services/ConnectDataBase';
import { getArrayInfoDataUpdate } from '../services/Functions';
import { updateInfoData } from '../redux/reducers';

/**
 * Hook personalizado para validación de datos de piezas
 * Reemplaza las funciones validateData repetidas en OperarioScreen y ScanScreen
 * 
 * @param {Object} options - Opciones de configuración
 * @returns {Object} Objeto con estado y funciones de validación
 */
const useValidation = (options = {}) => {
  const {
    autoDispatch = true, // Si debe hacer dispatch automático
    onValidationSuccess = null, // Callback cuando la validación es exitosa
    onValidationError = null, // Callback cuando hay error
    validationType = 2 // Tipo de validación para fullDataPiece
  } = options;

  const dispatch = useDispatch();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [lastValidatedCode, setLastValidatedCode] = useState(null);

  /**
   * Valida un código de pieza
   * @param {string} code - Código a validar
   * @returns {Promise<Object|null>} Datos validados o null si hay error
   */
  const validateData = useCallback(async (code) => {
    if (!code) {
      setValidationError('Código no proporcionado');
      return null;
    }

    setIsValidating(true);
    setValidationError(null);
    setLastValidatedCode(code);

    try {
      const dataOk = await fullDataPiece(code, validationType);
      
      if (dataOk) {
        // Dispatch automático si está habilitado
        if (autoDispatch) {
          const infoData = getArrayInfoDataUpdate(dataOk);
          dispatch(updateInfoData(infoData));
        }

        // Callback de éxito
        if (onValidationSuccess && typeof onValidationSuccess === 'function') {
          onValidationSuccess(dataOk, code);
        }

        return dataOk;
      } else {
        const errorMsg = 'No se encontraron datos para el código proporcionado';
        setValidationError(errorMsg);
        
        if (onValidationError && typeof onValidationError === 'function') {
          onValidationError(errorMsg, code);
        }
        
        return null;
      }
    } catch (error) {
      console.error('Error en validación:', error);
      const errorMsg = error.message || 'Error desconocido en la validación';
      setValidationError(errorMsg);
      
      if (onValidationError && typeof onValidationError === 'function') {
        onValidationError(errorMsg, code);
      }
      
      return null;
    } finally {
      setIsValidating(false);
    }
  }, [autoDispatch, onValidationSuccess, onValidationError, validationType, dispatch]);

  /**
   * Valida datos sin hacer dispatch (para casos especiales)
   * @param {string} code - Código a validar
   * @returns {Promise<Object|null>} Datos validados o null si hay error
   */
  const validateDataWithoutDispatch = useCallback(async (code) => {
    if (!code) {
      setValidationError('Código no proporcionado');
      return null;
    }

    setIsValidating(true);
    setValidationError(null);
    setLastValidatedCode(code);

    try {
      const dataOk = await fullDataPiece(code, validationType);
      
      if (dataOk) {
        // Callback de éxito
        if (onValidationSuccess && typeof onValidationSuccess === 'function') {
          onValidationSuccess(dataOk, code);
        }

        return dataOk;
      } else {
        const errorMsg = 'No se encontraron datos para el código proporcionado';
        setValidationError(errorMsg);
        
        if (onValidationError && typeof onValidationError === 'function') {
          onValidationError(errorMsg, code);
        }
        
        return null;
      }
    } catch (error) {
      console.error('Error en validación:', error);
      const errorMsg = error.message || 'Error desconocido en la validación';
      setValidationError(errorMsg);
      
      if (onValidationError && typeof onValidationError === 'function') {
        onValidationError(errorMsg, code);
      }
      
      return null;
    } finally {
      setIsValidating(false);
    }
  }, [onValidationSuccess, onValidationError, validationType]);

  /**
   * Limpia el estado de validación
   */
  const clearValidation = useCallback(() => {
    setValidationError(null);
    setLastValidatedCode(null);
    setIsValidating(false);
  }, []);

  /**
   * Valida y maneja el submit de un código (para formularios)
   * @param {string} code - Código a validar
   * @returns {Promise<Object|null>} Datos validados o null si hay error
   */
  const handleSubmitCode = useCallback(async (code) => {
    try {
      const fullData = await validateData(code);
      
      if (fullData) {
        return fullData;
      }
      
      return null;
    } catch (error) {
      console.error('Error en handleSubmitCode:', error);
      return null;
    }
  }, [validateData]);

  return {
    // Estado
    isValidating,
    validationError,
    lastValidatedCode,
    
    // Funciones
    validateData,
    validateDataWithoutDispatch,
    handleSubmitCode,
    clearValidation,
    
    // Setters directos
    setValidationError,
    setIsValidating
  };
};

export default useValidation;
